import { useUsersStore } from "@/stores/Users.store";
import { useOrganisationsStore } from "@/stores/Organisations.store";
import { UserRole } from "@/models/UserRole";

export class User {

	public id ?: string;
	public name ?: string;
	public email ?: string;
	public roles: UserRole[] = [];
	public impersonating: boolean = false;
	public locale ?: string;
	public capabilities: string[] = [];
	public avatar ?: string;
	public removed : boolean = false;

	public google_id ?: string;
	public microsoft_id ?: string;

	/**
	 * Entra tenant id of the Microsoft account the user signed in with.
	 * Only set after the user has signed in with Microsoft at least once since we started storing it.
	 */
	public microsoft_tenant_id ?: string;

	public superAdmin: boolean = false;

	public publicName ?: string;
	public publicDescription ?: string;
	public publicImage ?: {id: string};
	public authorType ?: string;
	public planCount ?: number;

	static createFromApiData(data: any): User {
		const user = new User();
        user.setFromServerData(data);

        return user;
	}

    static mapFromServer(data: any) {
        return User.createFromApiData(data);
    }

	getNameIdentifier(): string {
		if (this.name?.trim()) { // just in case a name consists of whitespace chars only (shouldn't occur though)
			return this.name;
		}

		if (this.email) {
			return this.email;
		}

		return 'User ' + this.id;
	}

	getPublicName(): string {
		return this.publicName || '?';
	}

	getPublicImage(): {id: string} | null {
		return this.publicImage || null;
	}

	getInitials(): string {

		if (this.name) {
			const nameParts = this.name.trim().split(/\s+/);
			if (nameParts.length === 1) {
				return nameParts[0].charAt(0).toUpperCase();
			} else {
				return (nameParts[0].charAt(0) + nameParts[nameParts.length - 1].charAt(0)).toUpperCase();
			}
		}

		if (this.email) {
			return this.email.charAt(0).toUpperCase();
		}

		return '?';
	}

	// gets initials based on the public name
	getPublicNameInitials(): string | null {
		if (this.publicName) {
			const nameParts = this.publicName.trim().split(/\s+/);
			if (nameParts.length === 1) {
				return nameParts[0].charAt(0).toUpperCase();
			} else {
				return (nameParts[0].charAt(0) + nameParts[nameParts.length - 1].charAt(0)).toUpperCase();
			}
		}
		return null;
	}

	/**
	 * Return the id of the user; throw exception if not defined.
	 * @returns string
	 */
	getId(): string {
		if (!this.id) {
			throw new Error('User has no id');
		}
		return this.id;
	}

    isMe(): boolean {
		const me = useUsersStore().me;
		if (!me) {
			return false;
		}

        return me.id === this.id;
    }

	isSuperAdmin(): boolean {
		return this.superAdmin && useOrganisationsStore().currentOrganisation?.name === 'SUPER';
	}

    setFromServerData(data: any)
    {
        this.id = data.id;
        this.name = data.name;

        this.impersonating = data.impersonating;
		this.locale = data.locale;
		this.email = data.email;

        if (data.roles) {
            this.roles = data.roles.map((orgData: any) => {
				return UserRole.mapFromServer(orgData);
			});
        }

		if (typeof(data.super_admin) !== 'undefined') {
			this.superAdmin = data.super_admin;
		}

		if (typeof(data.capabilities) !== 'undefined') {
			this.capabilities = data.capabilities;
		}

		if (typeof(data.avatar) !== 'undefined') {
			this.avatar = data.avatar;
		}

		if (typeof(data.removed) !== 'undefined') {
			this.removed = data.removed;
		}

		if (typeof(data.publicName) !== 'undefined') {
			this.publicName = data.publicName;
		}

		if (typeof(data.publicDescription) !== 'undefined') {
			this.publicDescription = data.publicDescription;
		}

		if (typeof(data.publicImage) !== 'undefined') {
			this.publicImage = data.publicImage;
		}

		if (typeof(data.authorType) !== 'undefined') {
			this.authorType = data.authorType;
		}

		if (typeof(data.planCount) !== 'undefined') {
			this.planCount = data.planCount;
		}

		this.google_id = data.google_id;
		this.microsoft_id = data.microsoft_id;
		this.microsoft_tenant_id = data.microsoft_tenant_id;
    }

    clone(): User {
		let clonedInstance = Object.assign(
			Object.create(
				Object.getPrototypeOf(this)
			),
			this
		);

		clonedInstance.roles = this.roles.map(org => org.clone());

		return clonedInstance;
	}

	getServerData(): any {
		return {
			id: this.id,
			name: this.name,
			email: this.email,
			roles: this.roles.map(
				(org) => {
					return {
						organisation: {
							id: org.organisation.id
						},
						role: org.role,
						active: org.active
					};
				}
			)
		};
	}

	/**
	 * Distinct school groups this user belongs to, via the active
	 * organisations' school group link.
	 */
	public getSchoolGroups(): { id: string, name: string }[] {
		const groups: { id: string, name: string }[] = [];
		this.roles.forEach(role => {
			if (!role.active) {
				return;
			}
			const group = role.organisation?.schoolGroup;
			if (group && !groups.find(g => g.id === group.id)) {
				groups.push(group);
			}
		});
		return groups;
	}

	/**
	 * Return the role associated to the current organisation set in the organisation store.
	 */
	public getCurrentOrganisationRole(): UserRole | null {
		return this.roles
			.find(org => org.organisation.id === useOrganisationsStore().currentOrganisation?.id) || null;
	}

	/**
	 * Magic currentOrganisationRole getter and setter for backwards compatability
	 * and easier access to the current organisation role
	 */
	public get role(): string {
		return this.getCurrentOrganisationRole()?.role || '';
	}

	public set role(value: string) {
		const organisationRole = this.getCurrentOrganisationRole();
		if (!organisationRole) {
			throw new Error('No current organisation set');
		}
		organisationRole.role = value;
	}

	public get active(): boolean {
		return this.getCurrentOrganisationRole()?.active || false;
	}

	public set active(value: boolean) {
		const organisationRole = this.getCurrentOrganisationRole();
		if (!organisationRole) {
			throw new Error('No current organisation set');
		}
		organisationRole.active = value;
	}

	hasOwnerPermissions() {
		return this.getCurrentOrganisationRole()?.hasOwnerPermissions() || false;
	}

	hasAdminPermissions() {
		return this.getCurrentOrganisationRole()?.hasAdminPermissions() || false;

	}

	hasTeacherPermissions() {
		return this.getCurrentOrganisationRole()?.hasTeacherPermissions() || false;
	}

	hasCapability(ability: string) {
		return this.capabilities.includes(ability);
	}
}
